import { getSupabaseClient } from '../lib/supabaseClient';
import type { DocumentMetadata } from './docStorageService';

export const USER_DOCUMENT_TYPES: Record<string, string> = {
  doc_protocol: 'Main study protocol',
  doc_consent_form: 'Consent form',
  doc_human_subject_training: 'Human subject training',
  doc_ind_fda_letter: 'FDA IND / expanded-access correspondence',
  doc_irb_auth_agreement: 'IRB authorization agreement',
  doc_administrative_action: 'Administrative action documentation',
  doc_study_personnel_resumes: 'Study personnel CV or resume',
  doc_investigator_brochure: "Investigator’s brochure",
  doc_recruitment_materials: 'Recruitment material',
  doc_data_collection_tools: 'Data collection tool',
  doc_additional_files: 'Supporting document / appendix',
  doc_fwa_documentation: 'Federalwide Assurance documentation',
  doc_cr_adverse_event_reports: 'Adverse event report',
  doc_cr_deviation_reports: 'Protocol deviation report',
  doc_cr_dsmb_reports: 'Safety monitoring report',
  doc_cr_progress_report: 'Continuing review progress report',
};

export function isSavedDocument(value: any): boolean {
  return Boolean(value && typeof value.id === 'string' && typeof value.file_path === 'string'
    && value.id && value.file_path && value.is_active === true && !value.isUploading && !value.error);
}

export function selectedDocumentIds(data: Record<string, any>): string[] {
  const ids: string[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (!key.startsWith('doc_') || key === 'doc_selection' || value == null) continue;
    const values = Array.isArray(value) ? value : [value];
    for (const document of values) {
      if (!isSavedDocument(document)) throw new Error('Finish or remove unfinished document uploads before submitting.');
      ids.push(document.id);
    }
  }
  return Array.from(new Set(ids));
}

function workflowError(error: any): Error {
  if (error?.code === 'PGRST202') return new Error('Document updates are temporarily unavailable. Please contact support.');
  return new Error(error?.message || 'The document update could not be saved. Please try again.');
}

export async function loadSubmissionDocuments(submissionId: string): Promise<DocumentMetadata[]> {
  const { data, error } = await getSupabaseClient().from('documents').select('*')
    .eq('submission_id', submissionId).like('type', 'doc_%').order('uploaded_at', { ascending: true });
  if (error) throw workflowError(error);
  return (data || []) as DocumentMetadata[];
}

export async function manageDocument(
  documentId: string, action: 'activate' | 'remove' | 'restore' | 'replace' | 'classify',
  type?: string, replacesId?: string,
): Promise<DocumentMetadata> {
  const { data, error } = await getSupabaseClient().rpc('manage_submission_document', {
    p_document_id: documentId, p_action: action, p_type: type || null, p_replaces_id: replacesId || null,
  });
  if (error) throw workflowError(error);
  const document = Array.isArray(data) ? data[0] : data;
  if (!document?.id) throw new Error('The document update was not saved. Please try again.');
  return document as DocumentMetadata;
}

export async function validateSubmissionDocuments(submissionId: string, data: Record<string, any>): Promise<void> {
  selectedDocumentIds(data);
  const { error } = await getSupabaseClient().rpc('validate_submission_documents', {
    p_submission_id: submissionId, p_form_data: data,
  });
  if (error) throw workflowError(error);
}

export async function finalizeSubmissionDocuments(
  submissionId: string, data: Record<string, any>, generatedDocumentIds: string[],
): Promise<void> {
  selectedDocumentIds(data);
  const { error } = await getSupabaseClient().rpc('finalize_submission_documents', {
    p_submission_id: submissionId, p_form_data: data, p_generated_document_ids: generatedDocumentIds,
  });
  if (error) throw workflowError(error);
}

/** Rebuild every document field from rows, clearing stale saved form references. */
export function documentFormValues(documents: DocumentMetadata[], previous: Record<string, any> = {}): Record<string, any> {
  const keys = new Set([...Object.keys(USER_DOCUMENT_TYPES), ...Object.keys(previous).filter(key => key.startsWith('doc_') && key !== 'doc_selection')]);
  const values: Record<string, any> = {};
  keys.forEach(key => {
    const rows = documents.filter(doc => doc.type === key && doc.is_active === true);
    values[key] = key === 'doc_protocol' ? rows[0] || null : rows.length ? rows : null;
  });
  const selection: Record<string, string> = {
    doc_study_personnel_resumes: 'Study Personnel Resumes/CVs',
    doc_investigator_brochure: "Investigator's Brochure",
    doc_recruitment_materials: 'Participant Recruitment Materials',
    doc_data_collection_tools: 'Data Collection Tools (CRFs / Questionnaires /Surveys)',
    doc_additional_files: 'Additional Document Types',
  };
  const selected = Object.entries(selection).filter(([key]) => values[key]?.length).map(([, text]) => text);
  values.doc_selection = selected.length ? selected : ['None of these documents apply to this study'];
  return values;
}
